import { RequestableType } from '../enums/RequestableType'
import { RequestStatus } from '../enums/RequestStatus'
import { ActionRequest, Company, Device, Project, RuuviTag, User } from '../types'

/**
 * Checks if the action request is closed.
 *
 * @param actionRequest - The action request to check.
 * @returns True if the action request is closed, false otherwise.
 */
export function isActionRequestClosed(actionRequest: ActionRequest) {
  return (
    actionRequest.status === RequestStatus.APPROVED ||
    actionRequest.status === RequestStatus.REJECTED
  )
}

type TypedActionRequest<
  TType extends RequestableType,
  TRequestable extends Project | Company | Device | User | RuuviTag
> = Omit<ActionRequest, 'requestableType' | 'requestable'> & {
  requestableType: TType
  requestable: TRequestable | undefined | null
}

function createActionRequestGuard<
  TType extends RequestableType,
  TRequestable extends Project | Company | Device | User | RuuviTag
>(
  type: TType
): (actionRequest: ActionRequest) => actionRequest is TypedActionRequest<TType, TRequestable> {
  return (actionRequest: ActionRequest): actionRequest is TypedActionRequest<TType, TRequestable> =>
    actionRequest.requestableType === type
}

export const isProjectActionRequest = createActionRequestGuard<RequestableType.PROJECT, Project>(
  RequestableType.PROJECT
)

export const isCompanyActionRequest = createActionRequestGuard<RequestableType.COMPANY, Company>(
  RequestableType.COMPANY
)

export const isDeviceActionRequest = createActionRequestGuard<RequestableType.DEVICE, Device>(
  RequestableType.DEVICE
)

export const isUserActionRequest = createActionRequestGuard<RequestableType.USER, User>(
  RequestableType.USER
)

export const isRuuviTagRequest = createActionRequestGuard<RequestableType.RUUVI_TAG, RuuviTag>(
  RequestableType.RUUVI_TAG
)
